import { Box, Stack, StackProps, SvgIcon, SxProps } from '@mui/material';
import React from 'react';

import SuppressedIcon from '@sbu/assets.svg/src/suppressed.svg';
import { Link, LinkProps } from '@sbu/components.link';
import { Tooltip } from '@sumologic/yama-ds-components';

import { useAppInfoQuery } from 'app/AppInfoQuery';
import { Indicator } from 'app/Entities/Indicator';
import { FederatedLink } from 'shared/components/internal/FederatedLink';
import { EntityReputationData } from 'shared/hooks/useEntityReputationsById';

export interface BaseEntityLinkProps {
  entityId: string;
  entityValue: string;
  sensorZone?: string | null;
  searchProps?: string;
  reputationData?: EntityReputationData;
  referrerProps?: Record<string, any>;
  showSuppression?: boolean;
  isSuppressed?: boolean;
  truncationStyleOverrides?: SxProps;
}

export interface FederatedEntityLinkProps extends BaseEntityLinkProps {
  federated: {
    tenant: string;
    tenantUrl?: string;
    deployment: string;
  };
}

export type EntityLinkProps = BaseEntityLinkProps | FederatedEntityLinkProps;

export const EntityLink = ({
  entityId,
  entityValue,
  sensorZone,
  searchProps,
  reputationData,
  referrerProps,
  showSuppression = false,
  isSuppressed,
  truncationStyleOverrides,
  ...restProps
}: EntityLinkProps) => {
  const { data: appData, loading: appLoading } = useAppInfoQuery();

  const urlEncodedId = encodeURIComponent(entityId);

  const label = `${entityValue} ${sensorZone || ''}`;
  const entityPath = `/entity?entityId=${urlEncodedId}${
    searchProps ? `&${searchProps}` : ''
  }`;

  const renderSuppression = () =>
    showSuppression &&
    isSuppressed && (
      <Tooltip title='Suppressed'>
        <span role='tooltip'>
          <SvgIcon
            component={SuppressedIcon}
            inheritViewBox
            sx={{
              height: 16,
              width: 16,
              color: 'icon',
            }}
          />
        </span>
      </Tooltip>
    );

  const renderIndicator = () =>
    reputationData?.reputation && (
      <Indicator
        entity={{
          id: entityId,
          ...reputationData,
        }}
        fetchData={false}
      />
    );

  const truncationStyles: SxProps = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '200px',
    ...truncationStyleOverrides,
  };

  const sharedProps: Pick<LinkProps, 'title' | 'state' | 'onClick' | 'sx'> = {
    title: label,
    onClick: (e) => e.stopPropagation(),
    sx: { ...truncationStyles, flexShrink: 1, minWidth: 0 },
    ...(referrerProps && { state: { referrerProps: { ...referrerProps } } }),
  };

  const stackProps: StackProps = {
    direction: 'row',
    gap: 0.5,
    alignItems: 'center',
    lineHeight: 'normal',
    sx: { minWidth: 0, width: '100%' },
  };

  if ('federated' in restProps) {
    const { federated } = restProps;

    if (appLoading) {
      return null;
    }

    const isFederatedCluster = !!appData?.isFederatedCluster;

    return isFederatedCluster ? (
      <Stack {...stackProps}>
        {federated.tenantUrl ? (
          <FederatedLink
            deployment={federated.deployment}
            tenantOrgId={federated.tenant}
            tenantUrl={federated.tenantUrl}
            href={entityPath}
            {...sharedProps}
          >
            {label}
          </FederatedLink>
        ) : (
          <Box sx={truncationStyles}>{label}</Box>
        )}
        {renderIndicator()}
        {renderSuppression()}
      </Stack>
    ) : (
      <Stack {...stackProps}>
        <Link href={`/sec${entityPath}`} {...sharedProps}>
          {label}
        </Link>
        {renderIndicator()}
        {renderSuppression()}
      </Stack>
    );
  }

  return (
    <Stack {...stackProps}>
      <Link
        data-sumo-test='entity-name'
        href={`/sec${entityPath}`}
        {...sharedProps}
      >
        {label}
      </Link>
      {renderIndicator()}
      {renderSuppression()}
    </Stack>
  );
};
